import React, { useMemo, useRef, useState } from "react";
import { Play, ChevronRight, ChevronLeft } from "lucide-react";

const CITY_PILLS = [
    "Trending",
    "Mumbai",
    "Bangalore",
    "Gurgaon",
    "Pune",
    "Noida",
    "Hyderabad",
    "Thane",
    "Navi Mumbai",
    "Chennai",
];

const VIDEOS = [
    {
        id: 1,
        city: "Bangalore",
        headline: "Bangalore's",
        headline2: "Smartest",
        tag: "Real Estate Bet?",
        thumb: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=900&q=80",
        video: "https://www.youtube.com/embed/dQw4w9WgXcQ",
    },
    {
        id: 2,
        city: "Noida",
        headline: "Riverside",
        headline2: "Living",
        tag: "In Noida?",
        thumb: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=900&q=80",
        video: "https://www.youtube.com/embed/dQw4w9WgXcQ",
    },
    {
        id: 3,
        city: "Mumbai",
        headline: "80% Units",
        headline2: "Sold",
        tag: "In 1 Day",
        thumb: "https://images.unsplash.com/photo-1567496898669-ee935f5f647a?w=900&q=80",
        video: "https://www.youtube.com/embed/dQw4w9WgXcQ",
    },
    {
        id: 4,
        city: "Gurgaon",
        headline: "This Isn't",
        headline2: "Dubai",
        tag: "It's Gurgaon",
        thumb: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=900&q=80",
        video: "https://www.youtube.com/embed/dQw4w9WgXcQ",
    },
    {
        id: 5,
        city: "Pune",
        headline: "Pune's",
        headline2: "Rising",
        tag: "Golden Corridor",
        thumb: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=900&q=80",
        video: "https://www.youtube.com/embed/dQw4w9WgXcQ",
    },
    {
        id: 6,
        city: "Hyderabad",
        headline: "Hyderabad's",
        headline2: "New",
        tag: "Wealth Belt",
        thumb: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=900&q=80",
        video: "https://www.youtube.com/embed/dQw4w9WgXcQ",
    },
    {
        id: 7,
        city: "Thane",
        headline: "Thane's",
        headline2: "Skyline",
        tag: "Boom Story",
        thumb: "https://images.unsplash.com/photo-1449844908441-8829872d2607?w=900&q=80",
        video: "https://www.youtube.com/embed/dQw4w9WgXcQ",
    },
    {
        id: 8,
        city: "Chennai",
        headline: "Chennai's",
        headline2: "Coastal",
        tag: "Luxury Wave",
        thumb: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=900&q=80",
        video: "https://www.youtube.com/embed/dQw4w9WgXcQ",
    },
    {
        id: 9,
        city: "Navi Mumbai",
        headline: "Navi Mumbai",
        headline2: "Growth",
        tag: "Airport Effect",
        thumb: "https://images.unsplash.com/photo-1554995207-c18c203602cb?w=900&q=80",
        video: "https://www.youtube.com/embed/dQw4w9WgXcQ",
    },
    {
        id: 10,
        city: "Mumbai",
        headline: "Mumbai's",
        headline2: "Trophy",
        tag: "Addresses",
        thumb: "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?w=900&q=80",
        video: "https://www.youtube.com/embed/dQw4w9WgXcQ",
    },
];

export default function VideoInsights() {
    const [pill, setPill] = useState("Trending");
    const [active, setActive] = useState(null); // video id opened in modal
    const scrollerRef = useRef(null);

    const items = useMemo(() => {
        if (pill === "Trending") return VIDEOS;
        return VIDEOS.filter((v) => v.city === pill);
    }, [pill]);

    const scroll = (dir) => {
        const el = scrollerRef.current;
        if (!el) return;
        el.scrollBy({ left: dir * (el.clientWidth * 0.75), behavior: "smooth" });
    };

    const activeVideo = active != null ? VIDEOS.find((v) => v.id === active) : null;

    return (
        <section className="bg-[var(--ebg-ivory)]" data-testid="home-video-insights">
            <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-24">
                {/* Heading */}
                <h2 className="font-serif text-4xl md:text-5xl lg:text-[52px] leading-[1.05] max-w-3xl">
                    Hot Selling Real Estate Projects in India
                </h2>
                <p className="text-black/60 mt-6 max-w-3xl leading-relaxed">
                    A handpicked collection of the country&rsquo;s most in-demand residential developments.
                    These properties, from modern apartments to premium villas, offer unmatched value in
                    top cities with ideal locations, smart amenities, and trusted builders.
                </p>

                {/* City pills */}
                <div className="mt-10 flex flex-wrap gap-3" data-testid="video-pills">
                    {CITY_PILLS.map((c) => (
                        <button
                            key={c}
                            data-testid={`video-pill-${c.toLowerCase().replace(/\s+/g,'-')}`}
                            onClick={() => setPill(c)}
                            className={`text-[13px] font-medium px-5 py-2.5 rounded-md border transition-all ${
                                pill === c
                                    ? "bg-black text-white border-black shadow-[0_4px_16px_-8px_rgba(0,0,0,0.6)]"
                                    : "bg-white text-black/75 border-black/10 hover:border-black hover:text-black"
                            }`}
                        >
                            {c}
                        </button>
                    ))}
                </div>

                {/* Video cards row */}
                <div className="relative mt-10">
                    <div
                        ref={scrollerRef}
                        className="flex gap-5 overflow-x-auto snap-x snap-mandatory scroll-smooth no-scrollbar -mx-6 md:-mx-10 px-6 md:px-10 pb-4"
                    >
                        {items.map((v, i) => (
                            <button
                                key={v.id}
                                type="button"
                                data-testid={`video-card-${v.id}`}
                                onClick={() => setActive(v.id)}
                                className="relative shrink-0 snap-start w-[65vw] sm:w-[42vw] md:w-[30vw] lg:w-[22vw] aspect-[3/5] overflow-hidden group text-left"
                                style={{ animationDelay: `${i * 60}ms` }}
                            >
                                {/* thumb */}
                                <img src={v.thumb} alt={v.city} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                                {/* dark gradient bottom */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/20" />
                                {/* Watermark */}
                                <span className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.24em] font-bold text-white/80 bg-black/40 backdrop-blur-md px-2 py-1">EBG Realty</span>

                                {/* Play button center */}
                                <span className="absolute inset-0 flex items-center justify-center">
                                    <span className="w-14 h-14 rounded-full bg-white/15 backdrop-blur-md border border-white/30 flex items-center justify-center text-white group-hover:bg-[var(--ebg-gold)] group-hover:text-black group-hover:scale-110 transition-all duration-300 shadow-[0_10px_30px_-8px_rgba(0,0,0,0.5)]">
                                        <Play size={22} fill="currentColor" />
                                    </span>
                                </span>

                                {/* Bottom overlay text */}
                                <div className="absolute inset-x-0 bottom-0 p-5">
                                    <div className="font-black uppercase leading-[0.95] text-white text-[26px] md:text-[28px] tracking-tight" style={{fontFamily: "'Manrope', 'Lato', sans-serif"}}>
                                        <div>{v.headline}</div>
                                        <div>{v.headline2}</div>
                                    </div>
                                    <div className="mt-2 inline-block bg-[#f5d066] text-black text-[13px] md:text-[14px] font-black uppercase px-2 py-0.5" style={{fontFamily: "'Manrope', 'Lato', sans-serif"}}>
                                        {v.tag}
                                    </div>
                                </div>
                            </button>
                        ))}
                        {items.length === 0 && (
                            <div className="w-full py-16 text-center text-black/40">No videos for {pill}.</div>
                        )}
                    </div>

                    {/* Scroll arrows */}
                    <button
                        type="button"
                        onClick={() => scroll(-1)}
                        data-testid="video-scroll-prev"
                        aria-label="Previous"
                        className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-11 h-11 rounded-full bg-white text-black border border-black/10 items-center justify-center shadow-[0_10px_30px_-10px_rgba(0,0,0,0.35)] hover:bg-black hover:text-white transition-colors"
                    >
                        <ChevronLeft size={18} />
                    </button>
                    <button
                        type="button"
                        onClick={() => scroll(1)}
                        data-testid="video-scroll-next"
                        aria-label="Next"
                        className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-11 h-11 rounded-full bg-white text-black border border-black/10 items-center justify-center shadow-[0_10px_30px_-10px_rgba(0,0,0,0.35)] hover:bg-black hover:text-white transition-colors"
                    >
                        <ChevronRight size={18} />
                    </button>
                </div>
            </div>

            {/* Video modal */}
            {activeVideo && (
                <div
                    className="fixed inset-0 bg-black/85 z-[80] flex items-center justify-center p-4"
                    role="dialog"
                    data-testid="video-modal"
                    onClick={() => setActive(null)}
                >
                    <div
                        className="relative w-full max-w-4xl aspect-video bg-black"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <iframe
                            title={activeVideo.city}
                            src={`${activeVideo.video}?autoplay=1`}
                            allow="autoplay; encrypted-media; picture-in-picture"
                            allowFullScreen
                            className="w-full h-full"
                        />
                        <button
                            type="button"
                            data-testid="video-modal-close"
                            onClick={() => setActive(null)}
                            className="absolute -top-10 right-0 text-white/80 hover:text-[var(--ebg-gold)] text-[11px] uppercase tracking-[0.28em] font-bold"
                        >
                            Close ×
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
}
